import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaEnvelope, FaComments, FaQuestionCircle, FaCheck, FaFacebookF, FaTwitter, FaInstagram, FaYoutube, FaChevronDown, FaChevronUp } from 'react-icons/fa';

const faqs = [
  {
    question: 'How does Pirooz help me learn Farsi?',
    answer: 'Pirooz uses a combination of spaced repetition, interactive exercises, and real-world context to help you learn Farsi naturally. Our AI adapts to your learning style and provides personalized feedback.'
  },
  {
    question: 'Is Pirooz suitable for complete beginners?',
    answer: 'Absolutely! Pirooz is designed for learners at all levels, from complete beginners to advanced speakers looking to refine their skills. Our curriculum starts with the basics of the Persian alphabet and builds up gradually.'
  },
  {
    question: 'Can I try Pirooz before subscribing?',
    answer: 'Yes! We offer a 7-day free trial with full access to all features. No credit card is required to start your trial, and you can cancel anytime during the trial period.'
  },
  {
    question: 'How does Pirooz handle Persian script?',
    answer: 'Pirooz teaches Persian script in a progressive manner with interactive writing exercises. We provide both Persian script and phonetic transliterations for beginners, gradually transitioning you to reading and writing in Persian script.'
  }
];

const Contact: React.FC = () => {
  const [chatOpen, setChatOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm({ ...form, [e.target.id]: e.target.value });
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    alert('Thank you for your message! Our team will get back to you soon.');
    setForm({ name: '', email: '', subject: '', message: '' });
  };

  return (
    <div className="bg-gray-50 min-h-screen" dir="ltr">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold mr-3">V</div>
              <h1 className="text-2xl font-bold text-gray-900">Pirooz</h1>
            </div>
            <nav className="hidden md:flex space-x-8">
              <Link to="/" className="text-gray-700 hover:text-blue-700 transition">Home</Link>
              <Link to="/features" className="text-gray-700 hover:text-blue-700 transition">Features</Link>
              <Link to="/pricing" className="text-gray-700 hover:text-blue-700 transition">Pricing</Link>
              <Link to="/contact" className="text-blue-700 font-semibold">Contact</Link>
            </nav>
            <div className="md:hidden">
              <button className="text-gray-700">
                <span className="sr-only">Open menu</span>
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Contact Pirooz</h2>
            <p className="text-lg text-gray-700 max-w-2xl mx-auto">
              Have questions about our Farsi language learning platform? Our team is here to help you with any inquiries.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white p-8 rounded-xl shadow-md">
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">Send us a message</h3>
              <form onSubmit={handleFormSubmit}>
                <div className="mb-6">
                  <label htmlFor="name" className="block text-gray-800 mb-2">Your Name</label>
                  <input type="text" id="name" value={form.name} onChange={handleFormChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div className="mb-6">
                  <label htmlFor="email" className="block text-gray-800 mb-2">Email Address</label>
                  <input type="email" id="email" value={form.email} onChange={handleFormChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div className="mb-6">
                  <label htmlFor="subject" className="block text-gray-800 mb-2">Subject</label>
                  <select id="subject" value={form.subject} onChange={handleFormChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">Select a topic</option>
                    <option value="account">Account Help</option>
                    <option value="technical">Technical Support</option>
                    <option value="feedback">Feedback</option>
                    <option value="business">Business Inquiry</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="mb-6">
                  <label htmlFor="message" className="block text-gray-800 mb-2">Your Message</label>
                  <textarea id="message" rows={5} value={form.message} onChange={handleFormChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"></textarea>
                </div>
                <button type="submit" className="w-full bg-blue-700 text-white py-3 px-6 rounded-lg hover:bg-blue-800 transition duration-300 font-semibold">
                  Send Message
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div>
              <div className="bg-white p-8 rounded-xl shadow-md mb-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-6">Contact Information</h3>
                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="bg-blue-100 p-3 rounded-full mr-4">
                      <FaEnvelope className="text-blue-500" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">Email Support</h4>
                      <a href="mailto:support@pingquack.com" className="text-blue-700 hover:underline">support@pingquack.com</a>
                      <p className="text-gray-700 text-sm mt-1">We typically respond within 24 hours</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="bg-blue-100 p-3 rounded-full mr-4">
                      <FaComments className="text-blue-500" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">Live Chat</h4>
                      <p className="text-gray-700">Available Monday-Friday, 9AM-5PM (GMT)</p>
                      <button className="mt-2 text-blue-700 hover:underline" onClick={() => setChatOpen(true)}>Start live chat</button>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="bg-blue-100 p-3 rounded-full mr-4">
                      <FaQuestionCircle className="text-blue-500" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">Help Center</h4>
                      <p className="text-gray-700">Find answers to common questions</p>
                      <Link to="/help" className="mt-2 text-blue-700 hover:underline">Visit help center</Link>
                    </div>
                  </div>
                </div>
              </div>

              {/* About Pirooz */}
              <div className="bg-blue-50 p-8 rounded-xl shadow-md">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">About Pirooz App</h3>
                <p className="text-gray-600 mb-6">
                  Pirooz is the premier platform for learning Farsi (Persian) through immersive, interactive experiences. Our app combines cutting-edge technology with proven language learning methodologies.
                </p>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="bg-blue-100 p-2 rounded-full mr-3 mt-1">
                      <FaCheck className="text-blue-500 text-sm" />
                    </div>
                    <p className="text-gray-800">Interactive lessons with native speakers</p>
                  </div>
                  <div className="flex items-start">
                    <div className="bg-blue-100 p-2 rounded-full mr-3 mt-1">
                      <FaCheck className="text-blue-500 text-sm" />
                    </div>
                    <p className="text-gray-800">AI-powered pronunciation feedback</p>
                  </div>
                  <div className="flex items-start">
                    <div className="bg-blue-100 p-2 rounded-full mr-3 mt-1">
                      <FaCheck className="text-blue-500 text-sm" />
                    </div>
                    <p className="text-gray-800">Cultural immersion through Persian media</p>
                  </div>
                  <div className="flex items-start">
                    <div className="bg-blue-100 p-2 rounded-full mr-3 mt-1">
                      <FaCheck className="text-blue-500 text-sm" />
                    </div>
                    <p className="text-gray-800">Personalized learning paths</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mt-16">
            <h3 className="text-3xl font-bold text-center text-gray-900 mb-8">Frequently Asked Questions</h3>
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="divide-y divide-gray-200">
                {faqs.map((faq, idx) => (
                  <div className="p-6" key={idx}>
                    <button className="flex justify-between items-center w-full text-left" onClick={() => setOpenFaq(openFaq === idx ? null : idx)}>
                      <h4 className="text-lg font-medium text-gray-900">{faq.question}</h4>
                      {openFaq === idx ? <FaChevronUp className="text-blue-500" /> : <FaChevronDown className="text-blue-500" />}
                    </button>
                    {openFaq === idx && (
                      <div className="mt-3 text-gray-700">
                        {faq.answer}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 mt-16">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold mr-3">P</div>
                <h3 className="text-xl font-bold">Pirooz</h3>
              </div>
              <p className="text-gray-300">
                The smartest way to learn Farsi, combining technology and human expertise for effective language acquisition.
              </p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-300 hover:text-white transition">Home</Link></li>
                <li><Link to="/features" className="text-gray-300 hover:text-white transition">Features</Link></li>
                <li><Link to="/pricing" className="text-gray-300 hover:text-white transition">Pricing</Link></li>
                <li><Link to="/blog" className="text-gray-300 hover:text-white transition">Blog</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Support</h4>
              <ul className="space-y-2">
                <li><Link to="/help" className="text-gray-300 hover:text-white transition">Help Center</Link></li>
                <li><Link to="/contact" className="text-gray-300 hover:text-white transition">Contact Us</Link></li>
                <li><Link to="/privacy" className="text-gray-300 hover:text-white transition">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-gray-300 hover:text-white transition">Terms of Service</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Connect With Us</h4>
              <div className="flex space-x-4 mb-4">
                <a href="https://facebook.com/piroozapp" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center hover:bg-blue-600 transition"><FaFacebookF /></a>
                <a href="https://twitter.com/piroozapp" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center hover:bg-blue-400 transition"><FaTwitter /></a>
                <a href="https://instagram.com/piroozapp" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center hover:bg-pink-600 transition"><FaInstagram /></a>
                <a href="https://youtube.com/piroozapp" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center hover:bg-red-600 transition"><FaYoutube /></a>
              </div>
              <p className="text-gray-300">
                Subscribe to our newsletter for tips and updates.
              </p>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 پیروز Pirooz. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Pirooz Buzz Chatbot */}
      <div className="fixed bottom-8 right-8 z-50">
        <div className="chatbot cursor-pointer" onClick={() => setChatOpen(!chatOpen)} style={{ width: 70, height: 70, backgroundColor: '#3b82f6', borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center', boxShadow: '0 4px 15px rgba(59, 130, 246, 0.5)' }}>
          <div className="chatbot-face" style={{ width: 50, height: 50, position: 'relative' }}>
            <div className="chatbot-eye" style={{ position: 'absolute', width: 8, height: 8, backgroundColor: 'white', borderRadius: '50%', top: 18, left: 12 }}></div>
            <div className="chatbot-eye" style={{ position: 'absolute', width: 8, height: 8, backgroundColor: 'white', borderRadius: '50%', top: 18, right: 12 }}></div>
            <div className="chatbot-mouth" style={{ position: 'absolute', width: 20, height: 8, borderBottomLeftRadius: 10, borderBottomRightRadius: 10, backgroundColor: 'white', bottom: 12, left: 15 }}></div>
          </div>
        </div>
        {chatOpen && (
          <div className="chatbot-bubble absolute bottom-20 right-0 bg-white p-4 rounded-xl shadow-xl max-w-xs animate-fade-in" style={{ zIndex: 1000 }}>
            <p className="text-sm">Hi there! I'm Pirooz Buzz, your learning assistant. How can I help you today?</p>
            <div className="mt-2 space-y-2">
              <button className="block w-full text-left text-xs bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded">How do I reset my password?</button>
              <button className="block w-full text-left text-xs bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded">What's included in the free trial?</button>
              <button className="block w-full text-left text-xs bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded">How does pronunciation practice work?</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Contact; 